import {
  EQUIPMENT,
  equipmentDefinition,
  sameEquipmentKey,
} from "@frontier/content/arsenal";
import type { EquipmentStack } from "@frontier/content/arsenal";
import type { Profile } from "@frontier/protocol/contracts";
import { EquipmentArt } from "./EquipmentArt";
import {
  contextAccepts,
  equipmentContextLabel,
  installedEquipmentSlot,
  mountedCount,
} from "./equipment-selection";
import type { EquipmentContext } from "./equipment-selection";
import { sameEquipmentFocus } from "./equipment-focus";
import type { EquipmentFocus } from "./equipment-focus";

export function EquipmentInventory({
  context,
  profile,
  selected,
  showBlueprints,
  market = false,
  onInspect,
  trainingInspect = false,
}: {
  context: EquipmentContext;
  profile: Profile;
  selected: EquipmentFocus | null;
  showBlueprints: boolean;
  market?: boolean;
  onInspect: (focus: EquipmentFocus) => void;
  trainingInspect?: boolean;
}) {
  const owned = profile.arsenal.stacks.filter(
    (stack) =>
      (profile.shipFitting || contextAccepts(context, stack.key)) &&
      stack.count > 0,
  );
  const entries: EquipmentStack[] = market
    ? EQUIPMENT.map((item) => ({
        key: item.key,
        tier: 0,
        count: profile.arsenal.stacks
          .filter(
            (stack) =>
              sameEquipmentKey(stack.key, item.key) && stack.tier === 0,
          )
          .reduce((sum, stack) => sum + stack.count, 0),
      }))
    : showBlueprints
      ? [
          ...owned,
          ...EQUIPMENT.filter(
            (item) =>
              contextAccepts(context, item.key) &&
              !owned.some((stack) => sameEquipmentKey(stack.key, item.key)),
          ).map((item) => ({ key: item.key, tier: 0 as const, count: 0 })),
        ]
      : owned;
  return (
    <section
      className="equipment-stock"
      aria-label={
        market
          ? "Магазин оснащения"
          : `Склад: ${equipmentContextLabel(context)}`
      }
    >
      <div className="equipment-stock-grid">
        {[...entries].sort((a, b) => trainingInspect ? Number(b.key.kind === "module" && b.key.id === "volley") - Number(a.key.kind === "module" && a.key.id === "volley") : 0).map((stack) => {
          const item = equipmentDefinition(stack.key);
          const installed = installedEquipmentSlot(
            profile,
            stack.key,
            stack.tier,
          );
          const inspected = sameEquipmentFocus(selected, stack);
          const mounted = mountedCount(profile, stack.key, stack.tier);
          const known = profile.arsenal.knownBlueprints.some((key) =>
            sameEquipmentKey(key, stack.key),
          );
          return (
            <button
              key={`${stack.key.kind}:${stack.key.id}:${stack.tier}`}
              className={`stock-tile ${installed ? "installed" : ""} ${inspected ? "inspected" : ""} ${stack.count === 0 ? "blueprint-tile" : ""}`}
              data-equipment-id={stack.key.id}
              data-equipment-tier={stack.tier}
              data-tutorial-highlight={
                (trainingInspect &&
                  stack.key.kind === "module" &&
                  stack.key.id === "volley" &&
                  stack.tier === 0) ||
                undefined
              }
              data-installed={installed !== null}
              aria-pressed={inspected}
              aria-label={`Осмотреть: ${item.name} MK ${"I".repeat(stack.tier + 1)}`}
              onClick={() => onInspect({ key: stack.key, tier: stack.tier })}
            >
              <span className="stock-art">
                <EquipmentArt equipment={stack.key} />
              </span>
              <span className="stock-grade">
                MK {"I".repeat(stack.tier + 1)}
              </span>
              {stack.count > 0 && (
                <span
                  className="stock-count"
                  title="Всего копий, включая установленные"
                >
                  ×{stack.count}
                </span>
              )}
              <b>{item.name}</b>
              <small>
                {market
                  ? known
                    ? "ОСМОТРЕТЬ · ПОКУПКА ОТКРЫТА"
                    : "ОСМОТРЕТЬ · ПОКУПКА ЗАКРЫТА"
                  : stack.count > 0
                    ? `Свободно ${Math.max(0, stack.count - mounted)} · на корабле ${mounted}${installed ? ` · ${installed}` : ""}`
                    : known
                      ? "МОЖНО ИЗГОТОВИТЬ"
                      : "ЧЕРТЁЖ ЗАКРЫТ"}
              </small>
            </button>
          );
        })}
      </div>
      {entries.length === 0 && (
        <p className="stock-empty-hint">Нет подходящего оснащения.</p>
      )}
    </section>
  );
}
